/**
 * The always-visible chrome, the phase-agnostic shell. At rest we show only the
 * chart, the vessel, four controls and one number. Everything else is summoned
 * into a sheet, used, and cleared away, because a tool that is on screen all the
 * time is one we have to mentally ignore all the time, and a beginner cannot
 * tell which buttons are safe to ignore.
 */
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useChartPlotterStore } from '@/store';
import { providerHasDepthData } from '@/chart-sources';
import { useSettingsStore } from '@/store/settings';
import { useSafetyStore } from '@/store/safety';
import { useShellStore, type DataField } from '@/store/shell';
import { tripDistanceNm } from '@/ownship/tripLog';
import { PRIMARY_TRACK_ID } from '@/store';
import { useGnssStore } from '@/store/gnss';
import { useAlarmsStore } from '@/store/alarms';
import { useEnvStore } from '@/store/environment';
import { useTideSourceStore } from '@/store/tideSources';
import { heightNowM } from '@/tides/tideNow';
import { depthUnderKeel } from '@/safety';
import { useRoutingStore, selectActiveRoute, selectActiveLeg, type ActiveLeg } from '@/store/routing';
import { formatLatLon, formatBearing } from '@/nav';
import { useBearingDisplayStore } from '@/store/bearingDisplay';
import {
  useUnitsStore,
  DEFAULT_UNITS,
  toDepth,
  depthUnitLabel,
  toDistance,
  distanceUnitLabel,
  toSpeed,
  speedUnitLabel,
  type UnitsPrefs,
} from '@/store/units';
import { summariseRouteTimed } from '@/routing';
import { LayersIcon, RouteIcon, RecentreIcon, BurgerIcon, ModeIcon } from './icons';
// Voice trigger parked for now (MOB took its slot in the control stack). Keep
// this import commented so bringing the mic back is a two-line change.
// import { VoiceControl } from './VoiceControl';
import { haptic } from '@/ui/haptics';
import { orientationBadge, orientationLabel } from '@/store/orientation';
import { MobButton } from './MobButton';
import { AnchorDropButton } from './AnchorDropButton';
import { GlossaryTerm } from '@/help/GlossaryTerm';
import { hasGlossaryEntry } from '@/help/glossary';
import { useAnchorWatchStore } from '@/store/anchorWatch';
import { navContext, primaryIndex } from '@/store/dataFieldConfig';

/**
 * Map a data-field's display label to a glossary key, so tapping the label
 * explains the term. Only the labels we have a plain-English entry for are here;
 * everything else (POSITION, TRIP, ETA…) falls through to plain text. Keyed on
 * the display string because that's what the readout hands us, and it can shift
 * (SOG falls back to POSITION when we're stopped), so we resolve it per render.
 */
const LABEL_GLOSSARY: Record<string, string> = {
  SOG: 'sog',
  COG: 'cog',
  HDG: 'hdg',
  XTD: 'xtd',
  CPA: 'cpa',
};

/** The label, wrapped as a tappable explainer when it's a term we can define. */
function FieldLabel({ label }: { label: string }) {
  const key = LABEL_GLOSSARY[label];
  if (key && hasGlossaryEntry(key)) {
    return <GlossaryTerm termKey={key}>{label}</GlossaryTerm>;
  }
  return <>{label}</>;
}

/** Short label shown above the data field. */
const FIELD_LABEL: Record<DataField, string> = {
  sog: 'SOG',
  cog: 'COG',
  heading: 'HDG',
  pos: 'POSITION',
  xte: 'XTD',
  brgwp: 'BRG WP',
  dtwp: 'DIST WP',
  eta: 'ETA',
  gnss: 'GNSS ACC',
  uke: 'U/KEEL',
  trip: 'TRIP',
  tide: 'TIDE',
  cpa: 'CPA',
};

export function Chrome() {
  const mode = useShellStore((s) => s.mode);
  // When a MOB is live the readout owns the very top of the screen, so drop the
  // top-bar controls below it rather than letting them collide.
  const mobActive = useSafetyStore((s) => s.mob != null);
  const openSheet = useShellStore((s) => s.openSheet);
  const setMode = useShellStore((s) => s.setMode);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const openMenuAt = useShellStore((s) => s.openMenuAt);
  const requestRecentre = useShellStore((s) => s.requestRecentre);
  const dataFields = useShellStore((s) => s.dataFields);
  const pinnedPrimary = useShellStore((s) => s.pinnedPrimary);
  // Armed anchor watch makes the primary readout context-aware (drag is what
  // you most want to catch), so subscribe to whether an anchor is set.
  const anchored = useAnchorWatchStore((s) => s.anchor != null);
  const units = useUnitsStore();
  const chartedDepthUnderBoatM = useGnssStore((s) => s.chartedDepthUnderBoatM);
  // Vector ENC carries depth; raster/tiles don't. Used to distinguish "no depth
  // data on this source" from a vector chart that just can't tell us here.
  const hasDepthData = useChartPlotterStore((s) => providerHasDepthData(s.chartProviderId));
  // Nearest AIS closest-point-of-approach, for the CPA data box (the strip's CPA
  // tile moved here). null when there's no target/threat, so the box reads "—".
  const cpa = useAlarmsStore((s) => s.live.ais?.cpa);
  const heightPortId = useEnvStore((s) => s.heightPortId);
  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  const heightSourceId = useTideSourceStore((s) => s.heightSourceId);
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  const overlayMode = useEnvStore((s) => s.overlayMode);
  const openScrubber = useEnvStore((s) => s.openScrubber);
  const draughtM = useSettingsStore((s) => s.draughtM);
  const setEditingFieldIndex = useShellStore((s) => s.setEditingFieldIndex);
  const orientation = useChartPlotterStore((s) => s.orientation);
  const streamOn = useChartPlotterStore((s) => s.groupVisibility['tidal-stream']);
  const tracking = useShellStore((s) => s.tracking);
  const openTrackMenu = useShellStore((s) => s.openTrackMenu);

  // Long-press on the orientation (north-up / head-up) control opens the same
  // track-actions popover the boat does — Edd asked for either target. A short
  // tap still just recentres / flips orientation; the ref suppresses that tap's
  // click once a long-press has fired.
  const orientPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orientLongFired = useRef(false);
  const openTrackMenuAt = (btn: HTMLButtonElement) => {
    const r = btn.getBoundingClientRect();
    openTrackMenu(r.left + r.width / 2, r.top + r.height / 2);
  };
  const startOrientPress = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const btn = e.currentTarget;
    orientLongFired.current = false;
    if (orientPressTimer.current) clearTimeout(orientPressTimer.current);
    orientPressTimer.current = setTimeout(() => {
      orientPressTimer.current = null;
      orientLongFired.current = true;
      openTrackMenuAt(btn);
    }, 450);
  };
  const cancelOrientPress = () => {
    if (orientPressTimer.current) {
      clearTimeout(orientPressTimer.current);
      orientPressTimer.current = null;
    }
  };

  // Long-press a Tide/height data box to jump straight to the full tide curve
  // (Conditions), the graph that sits behind the single number. Same 450 ms /
  // suppress-the-trailing-click pattern as the orientation control. Only one
  // box is pressed at a time, so a single timer + fired ref is enough.
  const fieldPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fieldLongFired = useRef(false);
  const startFieldPress = (field: string) => {
    fieldLongFired.current = false;
    if (fieldPressTimer.current) clearTimeout(fieldPressTimer.current);
    fieldPressTimer.current = setTimeout(() => {
      fieldPressTimer.current = null;
      // Other fields have no long-press yet, so the hold simply does nothing.
      if (field === 'tide') {
        fieldLongFired.current = true;
        haptic('select');
        openSheet('tides');
      }
    }, 450);
  };
  const cancelFieldPress = () => {
    if (fieldPressTimer.current) {
      clearTimeout(fieldPressTimer.current);
      fieldPressTimer.current = null;
    }
  };

  // --- Drag a data field up or down the stack to reorder it -------------------
  // A press that MOVES is a drag; a press that does not is still a tap, so the
  // picker and the tide long-press are untouched. We capture the pointer, so
  // the drag cannot leak through to the chart underneath and pan it.
  const dragRef = useRef<{ from: number; startY: number; pitch: number } | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const DRAG_SLOP_PX = 8;

  const onFieldPointerDown = (e: ReactPointerEvent<HTMLButtonElement>, index: number, field: string) => {
    startFieldPress(field);
    const el = e.currentTarget;
    const box = el.getBoundingClientRect();
    const stack = el.parentElement;
    // Row pitch drives how far a drag has to travel to move one place. Measured
    // from the two rendered boxes rather than assumed, so it stays right if the
    // gap or font size changes.
    const sibling = stack && stack.children.length > 1 ? (stack.children[index === 0 ? 1 : 0] as HTMLElement) : null;
    const pitch = sibling
      ? Math.abs(sibling.getBoundingClientRect().top - box.top) / Math.max(1, Math.abs((index === 0 ? 1 : 0) - index))
      : box.height + 6;
    dragRef.current = { from: index, startY: e.clientY, pitch: Math.max(1, pitch) };
  };

  const onFieldPointerMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const dy = e.clientY - d.startY;
    if (dragIndex == null) {
      if (Math.abs(dy) < DRAG_SLOP_PX) return;
      // It is a drag: cancel the long-press, take the pointer, and buzz once so
      // the lift is felt rather than guessed at.
      cancelFieldPress();
      fieldLongFired.current = true; // swallow the click that follows the drag
      haptic('select');
      setDragIndex(d.from);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* capture is a nicety; the drag still works without it */
      }
    }
  };

  const onFieldPointerUp = (e: ReactPointerEvent<HTMLButtonElement>) => {
    cancelFieldPress();
    const d = dragRef.current;
    dragRef.current = null;
    if (d && dragIndex != null) {
      const steps = Math.round((e.clientY - d.startY) / d.pitch);
      if (steps !== 0) {
        haptic('commit');
        useShellStore.getState().reorderDataField(d.from, d.from + steps);
      }
    }
    setDragIndex(null);
  };

  const ownship = useChartPlotterStore((s) => s.ownship);
  // Subscribe so the COG/HDG/BRG fields reformat the moment the True/Magnetic
  // reference or variation changes (formatBearing reads the global setting).
  useBearingDisplayStore((s) => s.mode);
  useBearingDisplayStore((s) => s.variationDeg);
  const tracks = useChartPlotterStore((s) => s.tracks);
  const tripNm = tripDistanceNm(tracks[PRIMARY_TRACK_ID]?.points ?? []);
  // Cruising/planning speed for ETA when we have no speed over the ground yet.
  const cruiseSpeedKn = useSettingsStore((s) => s.cruiseSpeedKn);
  // Whether that speed is over-ground (SOG) or through-water (STW). STW asks the
  // route preview to fold the tidal streams into the ETA.
  const planSpeedRef = useSettingsStore((s) => s.planSpeedRef);
  const activeRoute = useRoutingStore(selectActiveRoute);
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const activeLegIndex = useRoutingStore((s) => s.activeLegIndex);
  const followFromLegIndex = useRoutingStore((s) => s.followFromLegIndex);
  const goto = useRoutingStore((s) => s.goto);
  const routes = useRoutingStore((s) => s.routes);
  const marks = useRoutingStore((s) => s.marks);

  // The leg we are navigating to (a followed route or a goto mark) drives the
  // route data fields live. One shape, so the box code doesn't care which.
  const leg = selectActiveLeg({ followRouteId, activeLegIndex, followFromLegIndex, goto, routes, marks }, ownship.position);

  // Which box is the primary (the big number). Anchored > following a route >
  // underway > idle decides it, unless the skipper pinned a field themselves.
  const primaryIdx = primaryIndex(
    dataFields,
    navContext({ anchored, following: leg != null, sog: ownship.sog }),
    pinnedPrimary,
  );

  // One-way exit back to Live. Plotting is entered as a side-effect of the
  // chartwork tools (plot a clearing bearing, transit, heading, or clearing
  // depth on a feature), and the route-follow actions are the only other way
  // back to Live, so without this a user who plotted on a feature but has no
  // route to follow would be stranded in Plotting. This is a plain exit, not a
  // mode picker: it only ever returns to Live.
  const returnToLive = () => {
    haptic('select');
    // Finish any half-built route first, so the builder state doesn't linger
    // into Live (which can't reach it), matching the mode sheet's hand-off.
    const rs = useRoutingStore.getState();
    if (mode === 'Plotting' && rs.buildingRouteId) rs.finishRoute();
    setMode('Live');
    closeSheet();
    showToast('Live Navigation');
  };

  return (
    <>
      <div className={`acp-top-bar${mobActive ? ' mob-shift' : ''}`}>
        <button
          type="button"
          className="acp-icon-btn"
          title="Overlays"
          aria-label="Overlays"
          onClick={() => {
            haptic('select');
            openMenuAt('layers', 'overlays');
          }}
        >
          <LayersIcon />
        </button>

        <div className="acp-data-stack">
          {dataFields.map((f, i) => {
            const tideNowM = heightNowM({
              providerId: chartProviderId,
              heightSourceId,
              portId: heightPortId,
              ms: streamTimeMs,
            });
            const underKeelM = depthUnderKeel(chartedDepthUnderBoatM, tideNowM, draughtM);
            const r = readField(
              f,
              ownship,
              activeRoute,
              leg,
              underKeelM,
              cruiseSpeedKn,
              tripNm,
              tideNowM,
              cpa ?? null,
              units,
              hasDepthData,
              planSpeedRef,
              streamTimeMs,
            );
            return (
              <button
                key={i}
                type="button"
                className={`acp-data-field ${i === primaryIdx ? 'primary' : 'secondary'}`}
                title="Change or add a data field"
                aria-label={`Data field: ${r.label}. Tap to change.`}
                style={dragIndex === i ? { opacity: 0.6, transform: 'scale(1.03)' } : undefined}
                onPointerDown={(e) => onFieldPointerDown(e, i, f)}
                onPointerMove={onFieldPointerMove}
                onPointerUp={onFieldPointerUp}
                onPointerCancel={onFieldPointerUp}
                onPointerLeave={cancelFieldPress}
                onContextMenu={(e) => {
                  if (f === 'tide') {
                    e.preventDefault();
                    fieldLongFired.current = true;
                    haptic('select');
                    openSheet('tides');
                  }
                }}
                onClick={() => {
                  // A long-press already opened the tide curve; swallow its click.
                  if (fieldLongFired.current) {
                    fieldLongFired.current = false;
                    return;
                  }
                  if (f === 'tide' && streamOn && overlayMode === 'streams') {
                    haptic('select');
                    openScrubber();
                    return;
                  }
                  haptic('select');
                  setEditingFieldIndex(i);
                  openSheet('data');
                }}
              >
                <div className="label"><FieldLabel label={r.label} /></div>
                <div className={`value${r.isPos ? ' pos' : ''}`}>
                  {r.value}
                  {r.unit && <span className="unit"> {r.unit}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="acp-controls">
        {/* MOB sits where the mic used to. The voice trigger is parked for now;
            keep the import and this line so it's a one-line job to bring back. */}
        {/* <VoiceControl /> */}
        <MobButton />
        <button
          type="button"
          className="acp-ctrl"
          title="Menu"
          aria-label="Open the main menu"
          onClick={() => {
            haptic('select');
            openSheet('menu');
          }}
        >
          <BurgerIcon />
        </button>
        {mode !== 'Live' && (
          <button
            type="button"
            className="acp-ctrl"
            title="Return to Live"
            aria-label={`Return to Live navigation (currently ${mode})`}
            onClick={returnToLive}
          >
            <ModeIcon mode="Live" size={22} />
          </button>
        )}
        {mode !== 'Live' && (
          <button
            type="button"
            className="acp-ctrl"
            title="Route"
            aria-label="Route"
            onClick={() => { haptic('select'); openSheet('route'); }}
          >
            <RouteIcon />
          </button>
        )}
        {/* "Ready anchor" one-tap drop. Sits low in the stack, just above the
            recentre control and well clear of the MOB button at the top, so the
            safety-critical MOB can't be caught by mistake. Only present when the
            AnchorWatch control has armed it (AnchorDropButton returns null
            otherwise). */}
        <AnchorDropButton />
        <button
          type="button"
          className={`acp-ctrl acp-recentre${tracking ? ' tracking' : ''}`}
          title={
            tracking
              ? `Tracking the vessel (${orientationLabel(orientation).toLowerCase()}). Tap to cycle orientation; pan the chart to release.`
              : 'Centre on the vessel and track it as it moves.'
          }
          aria-label={
            tracking
              ? `Tracking the vessel, ${orientationLabel(orientation).toLowerCase()}. Tap to cycle orientation.`
              : 'Centre on the vessel and track it.'
          }
          aria-pressed={tracking}
          onPointerDown={startOrientPress}
          onPointerUp={cancelOrientPress}
          onPointerLeave={cancelOrientPress}
          onContextMenu={(e) => {
            e.preventDefault();
            orientLongFired.current = true;
            openTrackMenuAt(e.currentTarget);
          }}
          onClick={() => {
            // A long-press already opened the track menu; don't also recentre.
            if (orientLongFired.current) {
              orientLongFired.current = false;
              return;
            }
            haptic('select');
            requestRecentre();
          }}
        >
          <RecentreIcon />
          <span className="acp-orient">{orientationBadge(orientation)}</span>
        </button>
      </div>
    </>
  );
}

interface FieldRead {
  label: string;
  value: string;
  unit?: string;
  isPos?: boolean;
}

/**
 * Read the one data field. While the simulated boat sits still SOG reads zero,
 * so the field falls back to position until we are moving. Route-derived fields
 * need an active route; without one they show a dash rather than a wrong number.
 */
function readField(
  field: DataField,
  ownship: {
    position: [number, number] | null;
    sog: number;
    cog: number;
    heading: number;
    accuracyM?: number;
  },
  activeRoute: ReturnType<typeof selectActiveRoute>,
  leg: ActiveLeg | null,
  underKeelM: number | null,
  cruiseSpeedKn: number,
  tripNm = 0,
  tideNowM: number | null = null,
  cpa: { cpaNm: number; tcpaMin: number } | null = null,
  units: UnitsPrefs = DEFAULT_UNITS,
  hasDepthData = true,
  planSpeedRef: 'sog' | 'stw' = 'sog',
  nowMs = 0,
): FieldRead {
  const posOpts =
    units.position === 'dmm'
      ? ({ format: 'dmm', decimals: 2 } as const)
      : ({ format: units.position } as const);
  const posValue = ownship.position
    ? `${formatLatLon(ownship.position[1], 'lat', posOpts)}  ${formatLatLon(
        ownship.position[0],
        'lon',
        posOpts,
      )}`
    : 'No fix yet';

  switch (field) {
    case 'sog':
      // Fall back to position while sitting still, so the field is never a flat
      // zero that tells the skipper nothing.
      if (ownship.sog <= 0) {
        return { label: 'POSITION', value: posValue, isPos: true };
      }
      return { label: 'SOG', value: toSpeed(ownship.sog, units.speed).toFixed(1), unit: speedUnitLabel(units.speed) };
    case 'cog':
      return ownship.sog > 0
        ? { label: 'COG', value: formatBearing(ownship.cog) }
        : { label: 'COG', value: 'Not moving' };
    case 'heading':
      return { label: 'HDG', value: formatBearing(ownship.heading) };
    case 'pos':
      return { label: 'POSITION', value: posValue, isPos: true };
    case 'gnss': {
      // Straight from the GNSS fix: the receiver's own estimate, not invented
      // here. Sub-10 m shows a decimal so the gentle wander is visible.
      if (ownship.accuracyM == null) {
        return { label: 'GNSS ACC', value: 'No fix' };
      }
      const a = ownship.accuracyM;
      return { label: 'GNSS ACC', value: `±${a < 10 ? a.toFixed(1) : Math.round(a)}`, unit: 'm' };
    }
    case 'uke': {
      // Depth under the keel = charted depth here + height of tide now − draught.
      // Charted depth is published by MapView; null when the chart can't tell us.
      // A raster/tiles source has NO depth at all, which is different from a
      // vector chart that just can't tell us here — so we say so plainly.
      if (!hasDepthData) {
        return { label: 'U/KEEL', value: 'No depth data' };
      }
      if (underKeelM == null) {
        return { label: 'U/KEEL', value: '—' };
      }
      return { label: 'U/KEEL', value: toDepth(underKeelM, units.depth).toFixed(1), unit: depthUnitLabel(units.depth) };
    }
    case 'trip':
      return { label: 'TRIP', value: toDistance(tripNm, units.distance).toFixed(1), unit: distanceUnitLabel(units.distance) };
    case 'tide':
      // Height of tide now at the reference port. The "Tidal" favourite and the
      // Conditions sheet carry the scrubber and the full curve behind this number.
      if (tideNowM == null) {
        return { label: 'TIDE', value: '—' };
      }
      return { label: 'TIDE', value: toDepth(tideNowM, units.depth).toFixed(1), unit: depthUnitLabel(units.depth) };
    case 'cpa':
      // Nearest AIS closest point of approach; the AIS sheet holds the detail.
      if (cpa == null) {
        return { label: 'CPA', value: '—' };
      }
      return { label: 'CPA', value: toDistance(cpa.cpaNm, units.distance).toFixed(2), unit: distanceUnitLabel(units.distance) };
    case 'dtwp':
    case 'brgwp':
    case 'eta':
    case 'xte': {
      // Navigating (follow route or goto mark): live values against the leg.
      if (leg) {
        if (field === 'dtwp') {
          return { label: 'DIST WP', value: toDistance(leg.dtgNm, units.distance).toFixed(2), unit: distanceUnitLabel(units.distance) };
        }
        if (field === 'brgwp') {
          return { label: 'BRG WP', value: formatBearing(leg.brgDeg) };
        }
        if (field === 'xte') {
          return {
            label: 'XTD',
            value: `${toDistance(Math.abs(leg.xteNm), units.distance).toFixed(2)} ${leg.xteNm >= 0 ? 'R' : 'L'}`,
            unit: distanceUnitLabel(units.distance),
          };
        }
        // eta — use SOG while we're making way, else fall back to the boat's
        // cruising speed so the planning ETA still reads.
        {
          const spd = ownship.sog > 0 ? ownship.sog : cruiseSpeedKn;
          if (spd > 0) {
            const hrs = leg.remainingNm / spd;
            const h = Math.floor(hrs);
            const m = Math.round((hrs - h) * 60);
            return { label: 'ETA', value: `${h}h ${String(m).padStart(2, '0')}` };
          }
          return { label: 'ETA', value: '—' };
        }
      }

      // Not navigating: cross-track has no track to measure against.
      if (field === 'xte') {
        return { label: 'XTD', value: '—' };
      }
      // For the rest, show a static summary of the drawn route as a preview.
      if (!activeRoute || activeRoute.waypoints.length < 1) {
        return { label: FIELD_LABEL[field], value: 'No route' };
      }
      // STW mode folds the tidal streams into every leg (progressive, walking the
      // clock from the scrubber time). SOG stays the flat distance / speed, using
      // the live over-ground speed when we have it, else the cruising speed.
      const summary =
        planSpeedRef === 'stw'
          ? summariseRouteTimed(activeRoute, { ref: 'stw', knots: cruiseSpeedKn }, { departMs: nowMs })
          : summariseRouteTimed(activeRoute, { ref: 'sog', knots: ownship.sog || cruiseSpeedKn || 0 });
      if (field === 'dtwp') {
        return { label: 'DIST WP', value: summary.totalNm.toFixed(2), unit: 'nm' };
      }
      if (field === 'brgwp' && summary.legs.length) {
        return { label: 'BRG WP', value: formatBearing(summary.legs[0].bearingDeg) };
      }
      if (field === 'eta' && summary.totalHours !== undefined) {
        const h = Math.floor(summary.totalHours);
        const m = Math.round((summary.totalHours - h) * 60);
        // Say plainly which basis the time is on, and warn if a foul tide stalled
        // a leg so the number is a floor, not a real arrival.
        const label = summary.basis === 'stw-tide' ? 'ETA (tide)' : 'ETA';
        const value = `${h}h ${String(m).padStart(2, '0')}${summary.anyUnachievable ? ' !' : ''}`;
        return { label, value };
      }
      return { label: FIELD_LABEL[field], value: '—' };
    }
    default:
      return { label: 'POSITION', value: posValue, isPos: true };
  }
}
